import { useEffect, useState, type ReactNode } from 'react'
import type { ProductStub } from '@/data/products'
import { StarRating } from '@/components/StarRating'
import { useStorefront } from '@/state/StorefrontContext'
import { fetchProductImage } from '@/lib/productImage'

interface ProductCardProps {
  product:    ProductStub
  handle:     string   // specific Shopify handle for price lookup
  ratingKey:  string   // key into ratings map (cut name or cut-bib)
  ctaUrl:     string
  /**
   * Optional CTA override. Used for pre-launch products where the button
   * should read "Learn more" and deep-link to a PDP (signup form lives on
   * that page) instead of "Choose options" to a buyable variant.
   */
  ctaLabel?:  string
  footnote?:  ReactNode
  /**
   * Pre-launch chip shown over the image, e.g. "Arriving June 2026". Pulled
   * from product.comingSoon.label by the parent - passed in explicitly so
   * the card stays dumb and the result screen controls when the chip is
   * visible (e.g. only on the primary match, not on alternative cards).
   */
  comingSoonLabel?: string
  /**
   * Secondary chip rendered beneath the pre-launch chip. Clickable; used to
   * open the "need it sooner?" alternative modal. Kept as a generic prop so
   * the card doesn't have to know about the modal - the result screen
   * controls what happens on click.
   */
  secondaryChip?: { label: string; onClick: () => void }
  /**
   * Appended to the product name as " - <label>" so the card reflects the
   * length variant the quiz matched (e.g. "Mila Snow Pant - Short"). Length
   * is one of the things the quiz has just confirmed, so making it visible
   * on the card reassures the customer that the match respects their height.
   */
  lengthLabel?: string
}

export function ProductCard({
  product,
  handle,
  ratingKey,
  ctaUrl,
  ctaLabel = 'Explore options',
  footnote,
  comingSoonLabel,
  secondaryChip,
  lengthLabel,
}: ProductCardProps) {
  const { prices, ratings } = useStorefront()
  const { image, name, tagline } = product

  const price      = prices[handle]  ?? product.price
  const liveRating = ratings[ratingKey]

  const [liveImageUrl, setLiveImageUrl] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchProductImage(handle).then(url => {
      if (!cancelled && url) setLiveImageUrl(url)
    })
    return () => { cancelled = true }
  }, [handle])

  const imageUrl = liveImageUrl ?? image.url

  return (
    <article className="fq-product">
      <div
        className="fq-product-media"
        style={{ aspectRatio: `${image.width} / ${image.height}` }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={image.alt}
            width={image.width}
            height={image.height}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="fq-placeholder-label">
            {image.placeholderLabel}
            <br />
            <small>{image.width}×{image.height}</small>
          </span>
        )}
        {comingSoonLabel && (
          <span className="fq-product-chip" aria-label={comingSoonLabel}>
            {comingSoonLabel}
          </span>
        )}
        {secondaryChip && (
          <button
            type="button"
            className="fq-product-chip fq-product-chip--secondary"
            onClick={secondaryChip.onClick}
          >
            {secondaryChip.label}
          </button>
        )}
      </div>
      <div className="fq-product-body">
        <h3 className="fq-product-name">
          {tightenLastWord(name)}
          {lengthLabel && <span className="fq-product-length"> {lengthLabel}</span>}
        </h3>
        {liveRating && (
          <StarRating rating={liveRating.rating} count={liveRating.count} />
        )}
        <div className="fq-product-price">{price}</div>
        <p className="fq-product-tagline">{tagline}</p>
        <a className="fq-cta fq-cta--product" href={ctaUrl} target="_top" rel="noopener">
          {ctaLabel}
        </a>
        {footnote && <p className="fq-product-footnote">{footnote}</p>}
      </div>
    </article>
  )
}

// Keep the last two words together so wrapping never leaves "Pant" or "Bib"
// stranded alone on the final line (e.g. "Beatrice Snow\u00A0Pant").
function tightenLastWord(name: string): string {
  const parts = name.split(' ')
  if (parts.length < 2) return name
  const head = parts.slice(0, -2).join(' ')
  const tail = parts.slice(-2).join('\u00A0')
  return head ? `${head} ${tail}` : tail
}
